import React, { useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Chip,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { DataGrid, GridRenderCellParams } from '@mui/x-data-grid';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import {
  useRestrictionGet,
  useRestrictionStatusesGet,
  useRestrictionUpdate,
  RestrictionStatus,
} from '../../../clients/graphql/pages/restrictions-queries';

export const RestrictionDetailPage = () => {
  const { restrictionKey } = useParams<{ restrictionKey: string }>();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState<{
    value: number | '';
    pollFrequency: number | '';
    enabled: boolean;
    stripeProductId: string;
  }>({
    value: '',
    pollFrequency: '',
    enabled: false,
    stripeProductId: '',
  });

  const restrictionKeyNum = restrictionKey ? parseInt(restrictionKey) : undefined;

  const { loading: restrictionLoading, error: restrictionError, data: restrictionData, refetch: refetchRestriction } = 
    useRestrictionGet(restrictionKeyNum || 0, { skip: !restrictionKeyNum });
  
  const { loading: statusesLoading, error: statusesError, data: statusesData } = 
    useRestrictionStatusesGet(restrictionKeyNum, { skip: !restrictionKeyNum });

  const [updateRestriction] = useRestrictionUpdate();

  const restriction = restrictionData?.restrictionGet?.restriction;
  const statuses = statusesData?.restrictionStatusesGet?.restrictionStatuses || [];

  React.useEffect(() => {
    if (restriction) {
      setEditValues({
        value: restriction.value ?? '',
        pollFrequency: restriction.pollFrequency ?? '',
        enabled: restriction.enabled || false,
        stripeProductId: restriction.stripeProductId || '',
      });
    }
  }, [restriction]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (restriction) {
      setEditValues({
        value: restriction.value ?? '',
        pollFrequency: restriction.pollFrequency ?? '',
        enabled: restriction.enabled || false,
        stripeProductId: restriction.stripeProductId || '',
      });
    }
  };

  const handleSave = async () => {
    try {
      const result = await updateRestriction({
        variables: {
          input: {
            restrictionKey: restrictionKeyNum!,
            value: editValues.value === '' ? null : editValues.value,
            pollFrequency: editValues.pollFrequency === '' ? null : editValues.pollFrequency,
            enabled: editValues.enabled,
            stripeProductId: editValues.stripeProductId === '' ? null : editValues.stripeProductId,
          },
        },
      });
      
      // Check if the update was successful
      if (result.data?.restrictionUpdate?.success) {
        setIsEditing(false);  // Exit editing mode on success
        refetchRestriction();  // Refresh the data
      }
    } catch (error) {
      console.error('Error updating restriction:', error);
      // Keep editing mode open on error so user can fix issues
    }
  };

  if (restrictionLoading || statusesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (restrictionError) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, px: 2 }}>
        <Alert severity="error">
          Error fetching restriction details. Please reload the page.
        </Alert>
      </Box>
    );
  }

  if (!restriction) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, px: 2 }}>
        <Alert severity="warning">
          Restriction not found.
        </Alert>
      </Box>
    );
  }

  const statusColumns = [
    {
      field: 'restrictionStatusKey',
      headerName: 'ID',
      width: 80,
    },
    {
      field: 'entityName',
      headerName: 'Entity Name',
      width: 150,
    },
    {
      field: 'entityKey',
      headerName: 'Entity Key',
      width: 120,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params: GridRenderCellParams) => {
        const status = params.value as string;
        let color: 'success' | 'error' | 'warning' | 'info' = 'info';
        
        if (status === 'ACTIVE' || status === 'active') color = 'success';
        else if (status === 'INACTIVE' || status === 'inactive') color = 'error';
        else if (status === 'OVERRIDE' || status === 'override') color = 'warning';
        
        return (
          <Chip
            label={status}
            size="small"
            color={color}
          />
        );
      },
    },
    {
      field: 'overrideValue',
      headerName: 'Override Value',
      width: 140,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value !== null && params.value !== undefined ? params.value : 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleString()}
        </Typography>
      ),
    },
    {
      field: 'updatedAt',
      headerName: 'Updated',
      width: 180,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {new Date(params.value).toLocaleString()}
        </Typography>
      ),
    },
  ];

  const statusRows = statuses.map((status: RestrictionStatus, index: number) => ({
    id: index,
    ...status,
  }));

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', mt: 4, px: 2 }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/developer/restrictions')}
        >
          Back to Restrictions
        </Button>
      </Box>

      <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
        Restriction: {restrictionKey}
      </Typography>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Restriction Details</Typography>
          {!isEditing ? (
            <Button
              startIcon={<EditIcon />}
              onClick={handleEdit}
              variant="outlined"
            >
              Edit
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                startIcon={<SaveIcon />}
                onClick={handleSave}
                variant="contained"
                color="primary"
              >
                Save
              </Button>
              <Button
                startIcon={<CancelIcon />}
                onClick={handleCancel}
                variant="outlined"
              >
                Cancel
              </Button>
            </Box>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Type
            </Typography>
            <Typography variant="body1">{restriction.type || 'N/A'}</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Value
            </Typography>
            {isEditing ? (
              <TextField
                fullWidth
                type="number"
                value={editValues.value}
                onChange={(e) => setEditValues({ ...editValues, value: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                variant="outlined"
                size="small"
                inputProps={{ step: 'any' }}
              />
            ) : (
              <Typography variant="body1">{restriction.value !== null && restriction.value !== undefined ? restriction.value : 'N/A'}</Typography>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Poll Frequency
            </Typography>
            {isEditing ? (
              <TextField
                fullWidth
                type="number"
                value={editValues.pollFrequency}
                onChange={(e) => setEditValues({ ...editValues, pollFrequency: e.target.value === '' ? '' : parseInt(e.target.value) })}
                variant="outlined"
                size="small"
                InputProps={{ endAdornment: 'seconds' }}
              />
            ) : (
              <Typography variant="body1">
                {restriction.pollFrequency ? `${restriction.pollFrequency} seconds` : 'N/A'}
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Stripe Product ID
            </Typography>
            {isEditing ? (
              <TextField
                fullWidth
                value={editValues.stripeProductId}
                onChange={(e) => setEditValues({ ...editValues, stripeProductId: e.target.value })}
                variant="outlined"
                size="small"
                placeholder="Optional"
              />
            ) : (
              <Typography variant="body1">{restriction.stripeProductId || 'N/A'}</Typography>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Status
            </Typography>
            {isEditing ? (
              <FormControlLabel
                control={
                  <Switch
                    checked={editValues.enabled}
                    onChange={(e) => setEditValues({ ...editValues, enabled: e.target.checked })}
                  />
                }
                label={editValues.enabled ? 'Enabled' : 'Disabled'}
              />
            ) : (
              <Chip
                label={restriction.enabled ? 'Enabled' : 'Disabled'}
                color={restriction.enabled ? 'success' : 'error'}
                size="small"
              />
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Created At
            </Typography>
            <Typography variant="body1">
              {new Date(restriction.createdAt).toLocaleString()}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Updated At
            </Typography>
            <Typography variant="body1">
              {new Date(restriction.updatedAt).toLocaleString()}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Restriction Statuses
        </Typography>

        {statusesError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error fetching restriction statuses.
          </Alert>
        )}

        <Box sx={{ height: 400, width: '100%' }}>
          <DataGrid
            rows={statusRows}
            columns={statusColumns}
            loading={statusesLoading}
            rowHeight={45}
            sx={{
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#f5f5f5',
              },
            }}
            disableColumnMenu
            disableRowSelectionOnClick
          />
        </Box>
      </Paper>
    </Box>
  );
};